// src/pages/T432Request.jsx
// ════════════════════════════════════════════════════════════════
// T432 (Paragon) — Permission Request SHELL
// ────────────────────────────────────────────────────────────────
// Same scaffold as T290 (which mirrors T178/T321): side vertical stepper +
// content area, rendered INSIDE the app-wide <Layout> (no own top bar/hero/footer).
//
//   Step 1 — Setup       Smart department->team detection (CARDS), team selector,
//                        pre-approved vs unlisted routing, request type.
//   Step 2 — Permissions The 5 buckets (catalogs EMPTY for now — no T432 perm list yet).
//
// Smart detection:
//   - lookupDepartmentCards(alias)  -> requester's CARDS department (+ jobFamily)   [shared]
//   - listT432TeamConfigs()         -> configured T432 teams { teamLabel, orgName, orgAliases }
//   No match / empty config table -> defaults to UNLISTED (config table empty for now).
//
// Gated by REACT_APP_ENABLE_T432 (see LandingPage T432_ENABLED + App.js route).
// ════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { lookupDepartmentCards, listT432TeamConfigs } from '../services/api';

// ── Bucket definitions (Step 2). Each becomes ONE approval once the flow is built. ──
// Catalogs intentionally EMPTY until the T432 permission list is provided — populate
// each perm's { id, name, description, baseLevel?, link? }. Links not available yet —
// the row renderer only shows the badge/link when present, so they're omitted for now.
//
// T432 permissions are organised into THREE sections by Entity Type: API, APE, APUK.
// Permission types per section come from the T432 template table (Entity Type / Permission
// Type / Description). APE and APUK share the same set; API uses a combined "GL 1/GL2"
// gating tier instead of separate GL 1 / GL 2. "Non-SAM Teams" applies to all three.
const BUCKETS = [
    {
        id: 'api', title: 'API', description: 'API entity-type permissions.',
        icon: '', color: '#0073bb', bgColor: '#f0f8ff', borderColor: '#b8d4f0',
        perms: [
            { id: 't432_api_basic',       name: 'Basic Permissions',          description: 'Access to the multiple permission tiers — Investigation, Deep Dive, Review, Neonoir View.' },
            { id: 't432_api_nonfiling',   name: 'Non-Filing / Non-Filing View', description: 'Access to Non-Filing and Non-Filing View.' },
            { id: 't432_api_filing',      name: 'Filing / Filing View',       description: 'Access to Filing and Filing View permissions.' },
            { id: 't432_api_gl12',        name: 'GL 1 / GL 2',                description: 'Access to Gating Tier GL1 / GL2.' },
            { id: 't432_api_nonsam',      name: 'Non-SAM Teams',              description: 'APE/API/APUK access to the multiple permission tiers for teams outside of SAM.' },
        ],
    },
    {
        id: 'ape', title: 'APE', description: 'APE entity-type permissions.',
        icon: '', color: '#16a34a', bgColor: '#f0fdf4', borderColor: '#bbf7d0',
        perms: [
            { id: 't432_ape_basic',       name: 'Basic Permissions',          description: 'Access to the multiple permission tiers — Investigation, Deep Dive, Review, Neonoir View.' },
            { id: 't432_ape_nonfiling',   name: 'Non-Filing / Non-Filing View', description: 'Access to Non-Filing and Non-Filing View.' },
            { id: 't432_ape_filing',      name: 'Filing / Filing View',       description: 'Access to Filing and Filing View permissions.' },
            { id: 't432_ape_gl1',         name: 'GL 1',                       description: 'Access to Gating Tier GL1.' },
            { id: 't432_ape_gl2',         name: 'GL 2',                       description: 'Access to Gating Tier GL2.' },
            { id: 't432_ape_nonsam',      name: 'Non-SAM Teams',              description: 'APE/API/APUK access to the multiple permission tiers for teams outside of SAM.' },
        ],
    },
    {
        id: 'apuk', title: 'APUK', description: 'APUK entity-type permissions.',
        icon: '', color: '#7c3aed', bgColor: '#f5f3ff', borderColor: '#c4b5fd',
        perms: [
            { id: 't432_apuk_basic',      name: 'Basic Permissions',          description: 'Access to the multiple permission tiers — Investigation, Deep Dive, Review, Neonoir View.' },
            { id: 't432_apuk_nonfiling',  name: 'Non-Filing / Non-Filing View', description: 'Access to Non-Filing and Non-Filing View.' },
            { id: 't432_apuk_filing',     name: 'Filing / Filing View',       description: 'Access to Filing and Filing View permissions.' },
            { id: 't432_apuk_gl1',        name: 'GL 1',                       description: 'Access to Gating Tier GL1.' },
            { id: 't432_apuk_gl2',        name: 'GL 2',                       description: 'Access to Gating Tier GL2.' },
            { id: 't432_apuk_nonsam',     name: 'Non-SAM Teams',              description: 'APE/API/APUK access to the multiple permission tiers for teams outside of SAM.' },
        ],
    },
];

const STEPS = [
    { label: 'Setup', desc: 'Team & config' },
    { label: 'Permissions', desc: 'Select access' },
];

const slug = (s) => (s || '').toLowerCase().replace(/\s+/g, '_');

// ── Vertical stepper (same pattern as T178/T321/T290) ──
const VerticalStepper = ({ currentStep, steps }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
        {steps.map((s, idx) => {
            const isCompleted = idx < currentStep - 1;
            const isCurrent = idx === currentStep - 1;
            return (
                <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{
                            width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '14px', fontWeight: '700', flexShrink: 0,
                            background: isCompleted ? '#22c55e' : isCurrent ? '#ff9900' : '#e9ecef',
                            color: isCompleted ? '#fff' : isCurrent ? '#000' : '#999',
                            border: isCurrent ? '3px solid #ffcc80' : '2px solid transparent',
                            boxShadow: isCurrent ? '0 0 0 4px rgba(255,153,0,0.12)' : 'none'
                        }}>
                            {isCompleted ? '✓' : idx + 1}
                        </div>
                        {idx < steps.length - 1 && <div style={{ width: '2px', height: '28px', background: isCompleted ? '#22c55e' : '#e2e6ea', margin: '4px 0' }} />}
                    </div>
                    <div style={{ paddingTop: '6px' }}>
                        <div style={{ fontSize: '14px', fontWeight: isCurrent ? '700' : '500', color: isCurrent ? '#111' : isCompleted ? '#333' : '#999' }}>{s.label}</div>
                        <div style={{ fontSize: '12px', color: isCurrent ? '#555' : '#aaa', marginTop: '1px' }}>{s.desc}</div>
                    </div>
                </div>
            );
        })}
    </div>
);

// ── Page layout with stepper sidebar (same as T321/T290; renders inside app <Layout>) ──
const PageLayout = ({ title, subtitle, children, currentStep, steps }) => (
    <div style={{ width: '100%', padding: '0 40px 96px', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ marginBottom: '16px' }}>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111', margin: '0 0 2px 0' }}>{title}</h1>
            {subtitle && <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>{subtitle}</p>}
        </div>
        <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
            <div style={{ width: '170px', flexShrink: 0 }}>
                <div style={{ position: 'sticky', top: '72px' }}>
                    <VerticalStepper currentStep={currentStep} steps={steps} />
                </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        </div>
    </div>
);

const T432Request = ({ userAlias }) => {
    const [step, setStep] = useState(1);

    // ── Setup (Step 1) state ──
    const [isDepartmentLoading, setIsDepartmentLoading] = useState(true);
    const [departmentLoaded, setDepartmentLoaded] = useState(false);
    const [userDepartment, setUserDepartment] = useState('');
    const [userJobFamily, setUserJobFamily] = useState('');
    const [allTeamConfigs, setAllTeamConfigs] = useState([]);
    const [matchingTeams, setMatchingTeams] = useState([]);
    const [useCaseType, setUseCaseType] = useState(null);   // 'approved' | 'unlisted' | null
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [requestType, setRequestType] = useState(null);   // 'new' | 'rebaseline' | 'renewal'

    // ── Permissions (Step 2) state ──
    const [selected, setSelected] = useState(new Set());
    const [openBuckets, setOpenBuckets] = useState(() => BUCKETS.reduce((m, b) => { m[b.id] = true; return m; }, {}));
    const toggleBucketOpen = (bId) => setOpenBuckets(prev => ({ ...prev, [bId]: !prev[bId] }));

    const alias = userAlias || localStorage.getItem('ufs_user_alias') || '';
    const hasTeamMatch = departmentLoaded && matchingTeams.length > 0;

    // Smart department -> team detection (CARDS) + T432 team-config fetch.
    const loadDepartmentAndTeams = async () => {
        setIsDepartmentLoading(true);
        let detectedDept = '';
        try {
            if (alias) {
                try {
                    const result = await lookupDepartmentCards(alias);
                    if (result?.userDepartment) { setUserDepartment(result.userDepartment); detectedDept = result.userDepartment; }
                    if (result?.userJobFamily) setUserJobFamily(result.userJobFamily);
                } catch (e) {
                    if (e && e.isAuthError) {
                        const { handleAuthFailure } = await import('../services/auth');
                        setIsDepartmentLoading(false);
                        await handleAuthFailure();
                        return;
                    }
                    console.warn('[T432] Dept lookup failed (non-auth); continuing to team fetch:', e.message);
                }
            }

            let teams = [];
            for (let attempt = 0; attempt < 2 && teams.length === 0; attempt++) {
                try {
                    const r = await listT432TeamConfigs();
                    if (r?.teams) { setAllTeamConfigs(r.teams); teams = r.teams; }
                } catch (e) {
                    if (attempt === 0) await new Promise(res => setTimeout(res, 600));
                }
            }

            if (detectedDept && teams.length > 0) {
                const ud = detectedDept.toLowerCase().trim();
                const matched = teams.filter(t => (t.orgAliases || []).map(a => a.toLowerCase().trim()).includes(ud) || (t.orgName || '').toLowerCase().trim() === ud);
                setMatchingTeams(matched);
                if (matched.length === 0) setUseCaseType('unlisted');
            } else {
                setUseCaseType('unlisted');
            }
            setDepartmentLoaded(true);
        } catch (e) {
            setDepartmentLoaded(true);
        } finally {
            setIsDepartmentLoading(false);
        }
    };

    useEffect(() => { loadDepartmentAndTeams(); }, []); // eslint-disable-line

    const toggle = (permId) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(permId)) next.delete(permId); else next.add(permId);
            return next;
        });
    };

    const stepIndex = (label) => STEPS.findIndex(s => s.label === label) + 1;

    // ════════════════════════════════════════════════════════════════
    // STEP 1: SETUP
    // ════════════════════════════════════════════════════════════════
    if (step === 1) {
        return (
            <PageLayout title="🔐 T432 Permission Request" subtitle="Request access to Tenant 432 (Paragon) permissions" currentStep={stepIndex('Setup')} steps={STEPS}>
                {alias && (
                    <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                        <span style={{ fontSize: '16px' }}>👤</span>
                        <span style={{ fontWeight: '600', color: '#166534' }}>Requester: {alias}</span>
                        <span style={{ color: '#555' }}>— Authenticated via Midway</span>
                    </div>
                )}

                {isDepartmentLoading && <div style={{ padding: '16px', textAlign: 'center', fontSize: '13px', color: '#666' }}>⏳ Checking your department...</div>}

                {departmentLoaded && userDepartment && (
                    <div style={{ padding: '12px 16px', borderRadius: '8px', background: '#eef6ff', border: '1px solid #b8d4f0', marginBottom: '16px', fontSize: '14px' }}>
                        <span style={{ fontWeight: '600', color: '#0073bb' }}>🏢 Department:</span> {userDepartment}
                        {userJobFamily && <span style={{ marginLeft: '12px', color: '#555' }}>| Job Family: {userJobFamily}</span>}
                    </div>
                )}

                {/* Department matches configured team(s): show team selector */}
                {departmentLoaded && hasTeamMatch && (
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>🏢 Select Your Team</div>
                            <select
                                value={useCaseType === 'unlisted' ? '__not_listed__' : (selectedTeam?.teamLabel ? slug(selectedTeam.teamLabel) : '__not_selected__')}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '__not_listed__') { setUseCaseType('unlisted'); setSelectedTeam(null); }
                                    else if (val === '__not_selected__') { setUseCaseType(null); setSelectedTeam(null); }
                                    else {
                                        const sel = matchingTeams.find(t => slug(t.teamLabel) === val);
                                        if (sel) { setUseCaseType('approved'); setSelectedTeam(sel); }
                                    }
                                }}
                                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }}>
                                <option value="__not_selected__">-- Select your team --</option>
                                {matchingTeams.map(t => <option key={t.teamLabel} value={slug(t.teamLabel)}>{t.teamLabel}</option>)}
                                <option value="__not_listed__">My team is not listed</option>
                            </select>
                        </div>

                        {useCaseType === 'approved' && selectedTeam && (
                            <div style={{ padding: '14px 18px', borderRadius: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: '12px', fontSize: '14px' }}>
                                <span style={{ fontSize: '16px' }}>✅</span> <strong style={{ color: '#166534' }}>Your department has a Pre-Approved use case.</strong>
                                <div style={{ fontSize: '13px', color: '#555', marginTop: '6px' }}>Proceeding with the approved workflow for <strong>{selectedTeam.teamLabel}</strong>.</div>
                                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #bbf7d0', fontSize: '13px', color: '#555' }}>
                                    <span>Need to request different permissions? </span>
                                    <button type="button" onClick={() => { setUseCaseType('unlisted'); setSelectedTeam(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0073bb', fontSize: '13px', fontWeight: '600', textDecoration: 'underline', padding: 0 }}>Submit as Unlisted Use Case</button>
                                </div>
                            </div>
                        )}

                        {useCaseType === 'unlisted' && (
                            <div style={{ padding: '14px 18px', borderRadius: '8px', background: '#fffbeb', border: '1px solid #fde68a', marginBottom: '12px', fontSize: '14px' }}>
                                <span style={{ fontSize: '16px' }}>📋</span> <strong style={{ color: '#92400e' }}>Submitting as Unlisted Use Case.</strong>
                                <div style={{ fontSize: '13px', color: '#555', marginTop: '6px' }}>You'll proceed with the unlisted workflow, which requires Leadership Approval.</div>
                                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #fde68a', fontSize: '13px', color: '#555' }}>
                                    <span>Found your team? </span>
                                    <button type="button" onClick={() => { setUseCaseType(null); setSelectedTeam(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#22c55e', fontSize: '13px', fontWeight: '600', textDecoration: 'underline', padding: 0 }}>Switch back to team selection</button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* No match (incl. empty config table): unlisted, with manual pre-approved fallback */}
                {departmentLoaded && !hasTeamMatch && (
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{ padding: '14px 18px', borderRadius: '8px', background: '#fffbeb', border: '1px solid #fde68a', fontSize: '14px', marginBottom: '12px' }}>
                            <span style={{ fontSize: '16px' }}>📋</span> <strong style={{ color: '#92400e' }}>We couldn't auto-match your department to a Pre-Approved team.</strong>
                            <div style={{ fontSize: '13px', color: '#555', marginTop: '6px' }}>Select your team below if it's listed, or proceed as an Unlisted use case (requires Leadership Approval).</div>
                        </div>

                        <div style={{ marginBottom: '4px' }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>🏢 Select Your Team</div>
                            <select
                                value={useCaseType === 'approved' && selectedTeam?.teamLabel ? slug(selectedTeam.teamLabel) : (useCaseType === 'unlisted' ? '__not_listed__' : '__not_selected__')}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '__not_listed__') { setUseCaseType('unlisted'); setSelectedTeam(null); }
                                    else if (val === '__not_selected__') { setUseCaseType(null); setSelectedTeam(null); }
                                    else {
                                        const sel = allTeamConfigs.find(t => slug(t.teamLabel) === val);
                                        if (sel) { setUseCaseType('approved'); setSelectedTeam(sel); }
                                    }
                                }}
                                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }}>
                                <option value="__not_selected__">-- Select your team --</option>
                                {allTeamConfigs.map(t => <option key={t.teamLabel} value={slug(t.teamLabel)}>{t.teamLabel}</option>)}
                                <option value="__not_listed__">My team is not listed (Unlisted use case)</option>
                            </select>
                        </div>

                        {useCaseType === 'approved' && selectedTeam && (
                            <div style={{ padding: '14px 18px', borderRadius: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0', marginTop: '12px', fontSize: '14px' }}>
                                <span style={{ fontSize: '16px' }}>✅</span> <strong style={{ color: '#166534' }}>Pre-Approved use case selected.</strong>
                                <div style={{ fontSize: '13px', color: '#555', marginTop: '6px' }}>Proceeding with the approved workflow for <strong>{selectedTeam.teamLabel}</strong>.</div>
                            </div>
                        )}
                    </div>
                )}

                {/* Request Type — after the use-case type is resolved */}
                {departmentLoaded && (useCaseType === 'approved' || useCaseType === 'unlisted') && (
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>📋 Request Type</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <button type="button" onClick={() => setRequestType('new')}
                                style={{ padding: '16px', borderRadius: '10px', cursor: 'pointer', border: `2px solid ${requestType === 'new' ? '#ff9900' : '#e2e6ea'}`, background: requestType === 'new' ? '#fffbf0' : '#fff', textAlign: 'left', position: 'relative' }}>
                                {requestType === 'new' && <span style={{ position: 'absolute', top: '10px', right: '12px', width: '22px', height: '22px', borderRadius: '50%', background: '#ff9900', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700' }}>✓</span>}
                                <div style={{ fontWeight: '700', fontSize: '14px', color: requestType === 'new' ? '#92400e' : '#333' }}>🆕 New Request</div>
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{useCaseType === 'approved' ? 'Request new permissions for your team.' : 'First-time access request.'}</div>
                            </button>
                            <button type="button" onClick={() => setRequestType(useCaseType === 'approved' ? 'rebaseline' : 'renewal')}
                                style={{ padding: '16px', borderRadius: '10px', cursor: 'pointer', border: `2px solid ${(requestType === 'rebaseline' || requestType === 'renewal') ? '#0073bb' : '#e2e6ea'}`, background: (requestType === 'rebaseline' || requestType === 'renewal') ? '#eef6ff' : '#fff', textAlign: 'left', position: 'relative' }}>
                                {(requestType === 'rebaseline' || requestType === 'renewal') && <span style={{ position: 'absolute', top: '10px', right: '12px', width: '22px', height: '22px', borderRadius: '50%', background: '#0073bb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700' }}>✓</span>}
                                <div style={{ fontWeight: '700', fontSize: '14px', color: (requestType === 'rebaseline' || requestType === 'renewal') ? '#0073bb' : '#333' }}>{useCaseType === 'approved' ? '🔄 Rebaseline' : '🔄 Renewal'}</div>
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{useCaseType === 'approved' ? 'Extend existing approved access.' : 'Re-request access after expiry.'}</div>
                            </button>
                        </div>
                    </div>
                )}

                {/* Continue — full-width orange, matching T321/T290 */}
                {departmentLoaded && requestType && (useCaseType === 'approved' || useCaseType === 'unlisted') && (
                    <button onClick={() => setStep(2)} disabled={useCaseType === 'approved' && !selectedTeam}
                        style={{ width: '100%', padding: '14px', borderRadius: '10px', border: 'none', background: (useCaseType === 'approved' && !selectedTeam) ? '#e2e6ea' : '#ff9900', color: (useCaseType === 'approved' && !selectedTeam) ? '#888' : '#000', fontSize: '15px', fontWeight: '700', cursor: (useCaseType === 'approved' && !selectedTeam) ? 'not-allowed' : 'pointer' }}>
                        Continue →
                    </button>
                )}
            </PageLayout>
        );
    }

    // ════════════════════════════════════════════════════════════════
    // STEP 2: PERMISSIONS (5 buckets)
    // ════════════════════════════════════════════════════════════════
    return (
        <PageLayout title="🔐 T432 Permission Request" subtitle="Select permissions across buckets" currentStep={stepIndex('Permissions')} steps={STEPS}>
            <button type="button" onClick={() => setStep(1)} style={{ padding: '10px 18px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', border: '2px solid #0073bb', background: '#f0f8ff', color: '#0073bb', marginBottom: '16px' }}>← Back to Setup</button>

            {/* Context strip — carries the Setup selections forward */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px', fontSize: '12px' }}>
                <span style={{ padding: '4px 10px', borderRadius: '12px', background: useCaseType === 'approved' ? '#dcfce7' : '#fef9c3', color: useCaseType === 'approved' ? '#166534' : '#854d0e', fontWeight: '700' }}>
                    {useCaseType === 'approved' ? `Pre-Approved${selectedTeam ? ` — ${selectedTeam.teamLabel}` : ''}` : 'Unlisted Use Case'}
                </span>
                {requestType && <span style={{ padding: '4px 10px', borderRadius: '12px', background: '#eef6ff', color: '#0073bb', fontWeight: '700' }}>{requestType === 'new' ? 'New Request' : requestType === 'rebaseline' ? 'Rebaseline' : 'Renewal'}</span>}
            </div>

            <h3 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>Select Permissions</h3>
            <p style={{ fontSize: '14px', color: '#666', margin: '0 0 20px' }}>Permissions are organised by entity type (API, APE, APUK). Select the permission types you need under each.</p>

            {BUCKETS.map(bucket => {
                const isOpen = !!openBuckets[bucket.id];
                const selCount = bucket.perms.filter(p => selected.has(p.id)).length;
                return (
                    <div key={bucket.id} style={{ border: `1px solid ${selCount > 0 ? bucket.borderColor : '#d1d5db'}`, borderRadius: '10px', overflow: 'hidden', marginBottom: '12px' }}>
                        {/* Collapsible header — rotating arrow + count + Minimise/Expand (same as T321/T290) */}
                        <div onClick={() => toggleBucketOpen(bucket.id)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer', background: selCount > 0 ? bucket.bgColor : '#f8fafc', borderBottom: isOpen ? `1px solid ${bucket.borderColor}` : 'none' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '13px', transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', color: '#64748b' }}>▶</span>
                                {bucket.icon && <span style={{ fontSize: '16px' }}>{bucket.icon}</span>}
                                <span style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>{bucket.title}</span>
                                {selCount > 0 && (
                                    <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', background: bucket.color, color: '#fff', fontWeight: '700' }}>{selCount} selected</span>
                                )}
                            </div>
                            <span style={{ fontSize: '12px', color: '#0073bb', fontWeight: '600' }}>{isOpen ? 'Minimise ▲' : `Expand (${bucket.perms.length}) ▼`}</span>
                        </div>
                        {isOpen && (
                            <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {bucket.perms.length === 0 ? (
                                    <div style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>No permissions configured yet.</div>
                                ) : (
                                    bucket.perms.map(perm => {
                                        const isSel = selected.has(perm.id);
                                        return (
                                            <div key={perm.id} onClick={() => toggle(perm.id)}
                                                style={{ padding: '10px 14px', borderRadius: '8px', border: `2px solid ${isSel ? bucket.color : '#e5e7eb'}`, background: isSel ? bucket.bgColor : '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{ width: '20px', height: '20px', borderRadius: '5px', border: `2px solid ${isSel ? bucket.color : '#d1d5db'}`, background: isSel ? bucket.color : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                    {isSel && <span style={{ color: '#fff', fontSize: '11px', fontWeight: '700' }}>✓</span>}
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{perm.name}</div>
                                                    {perm.description && <div style={{ fontSize: '11px', color: '#64748b', marginTop: '1px' }}>{perm.description}</div>}
                                                </div>
                                                {perm.baseLevel && <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', background: '#f1f5f9', color: '#475569', flexShrink: 0, fontWeight: '600' }}>L{perm.baseLevel}</span>}
                                                {perm.link && <a href={perm.link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                                                    style={{ padding: '4px 10px', borderRadius: '5px', fontSize: '11px', fontWeight: '600', color: '#0073bb', textDecoration: 'none', border: '1px solid #b8d4f0', background: '#f0f8ff', whiteSpace: 'nowrap', flexShrink: 0 }}>View ↗</a>}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" disabled title="Submission will be enabled once the T432 flow is built out."
                    style={{ padding: '12px 28px', borderRadius: '8px', border: 'none', background: '#e2e8f0', color: '#94a3b8', fontSize: '14px', fontWeight: '700', cursor: 'not-allowed' }}>Continue →</button>
            </div>
        </PageLayout>
    );
};

export default T432Request;
